import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';

import {
  CoverImage,
  RankingSelector,
  SidebarContentView,
  Statistics,
} from './components';

import { Button, Form, InputField, Label, Loader, Modal } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { IconFigure } from '@/components/Icons';
import InputError from '@/components/InputError';
import { PencilIcon } from '@/components/Navbar/Icons';
import { DefaultTabsProps, Tabs } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  changeTDAtoVip,
  getAvailablePinsTDAVIP,
  getPinsValidationTDAVIP,
  getRankingAvailable,
  getTdaById,
  getAdminTda,
} from '@/services/tda';
import { ResponseRankingTda, ViewTDAResponse } from '@/types/tda';
import {
  GENERICS_WORDS,
  ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS,
  cn,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const initialState = {
  showModal: false,
  showChangeToVipModal: false,
  showDesktopVideoModal: false,
  convertButtonDisabled: true,
  pinNumber1: '',
  pinNumber2: '',
  pinErrorMessage: '',
};

export const ViewTourney: React.FC = () => {
  const { userInitialData, isLoading } = useAuth();
  const { tab: tabsTeam, handleTabChanges: handleTabsTeam } =
    useHandleChangesTabs();
  const { isMobile, isDesktop } = useIsDesktop();
  const { tourneyData, handleSetTourney } = useTourney();

  const [stateTourney, setStateTourney] = useState(initialState);

  const { pinsData } = usePinsAccount();

  const navigate = useNavigate();
  const toast = useToast();

  // servicio de ranking para paginar por fechas
  const { isLoading: isLoadingRanking, refetch: refetchDataRanking } = useQuery(
    'initRankingTda',
    async () => await getRankingAvailable(tourneyData.initDay || 0),
    {
      onSuccess: async (response: ResponseRankingTda) => {
        if (response.estado) {
          const matchDay =
            response.fechasConRanking[response.fechasConRanking.length - 1] ||
            response.fechas.filter(
              (day) =>
                day.orden === tourneyData.initDay ||
                day.id === tourneyData.initDay
            )[0];

          const isF11 = tabsTeam === TabType.FirstTab;
          const responseTourney = await getTdaById(
            tourneyData.id,
            isF11,
            matchDay.id
          );

          const responseAdmin = (tourneyData.isOwner &&
            (await getAdminTda(tourneyData.id))) || { estado: false };

          handleSetTourney({
            ...tourneyData,
            mainTab: TabType.FirstTab,
            subMainTab: TabType.FirstTab,
            matchDay,
            rankingAvailable: response.fechasConRanking,
            ...(responseAdmin.estado && {
              pendingInvitations: responseAdmin.pageAcep.objectCount,
            }),
            ...(responseTourney.estado && {
              tourney: responseTourney,
              statitics: responseTourney.estadisticasVip,
              records: responseTourney.recordsTDA,
              isVip: responseTourney.vip,
              selectedCover: {
                id: responseTourney.portada?.idPortadaPredefinida,
                url: responseTourney.portada?.pathImagen,
              },
              ...(responseAdmin.estado && {
                tourneyGuests: responseAdmin.pageInvitados.objectList,
                tourneyPlayers: responseAdmin.pagePart.objectList,
                tourneyPendings: responseAdmin.pageAcep.objectList,
              }),
            }),
          });
        } else {
          console.error('Error obteniendo datos de inicio del Torneo.');
        }
      },
      onError: (error) => {
        console.error('Error obteniendo datos de inicio del Torneo:', error);
        throw new Error('Invalid response state');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: Boolean(tourneyData.initDay) && Boolean(tourneyData.id),
    }
  );

  // servicio de fechas segun id
  const { isLoading: isLoadingDataTourney, refetch: refetchDataTourney } =
    useQuery(
      ['fechaId', tourneyData.matchDay?.id, tabsTeam],
      async () => {
        if (tourneyData.matchDay?.id) {
          const isF11 = tabsTeam === TabType.FirstTab;
          return await getTdaById(
            tourneyData.id,
            isF11,
            tourneyData.matchDay?.id
          );
        }
      },
      {
        refetchOnWindowFocus: false,
        keepPreviousData: false,
        enabled: !!tourneyData.matchDay?.id,
        onSuccess: async (response: ViewTDAResponse) => {
          if (response?.estado) {
            handleSetTourney({
              ...tourneyData,
              mainTab: tabsTeam,
              tourney: response,
            });
          } else {
            console.error('Error obteniendo datos de inicio del Torneo.');
          }
        },
        onError: (error) => {
          console.error('Error obteniendo datos de inicio del Torneo:', error);
          throw new Error('Invalid response state');
        },
      }
    );

  // manejo de datos de tabla por id de fechas
  useUpdateEffect(() => {
    tourneyData.matchDay?.id && refetchDataTourney();
  }, [tabsTeam, tourneyData.matchDay?.id]);

  useEffect(() => {
    if (!pinsData.isPremium) {
      setStateTourney((prevState) => ({ ...prevState, pinErrorMessage: '' }));
      (async () => {
        const response = await getAvailablePinsTDAVIP();
        if (response.estado) {
          if (response.pin1 !== undefined) {
            setStateTourney((prevState) => ({
              ...prevState,
              pinNumber1: response.pin1,
            }));
          }
          if (response.pin2 !== undefined) {
            setStateTourney((prevState) => ({
              ...prevState,
              pinNumber2: response.pin2,
            }));
          }
        }
      })();
    }
  }, [pinsData.isPremium, stateTourney.showChangeToVipModal]);

  useEffect(() => {
    setStateTourney((prevState) => ({
      ...prevState,
      convertButtonDisabled:
        !pinsData.isPremium &&
        (!stateTourney.pinNumber1.length || !stateTourney.pinNumber2.length),
    }));
  }, [
    pinsData.isPremium,
    stateTourney.pinNumber1.length,
    stateTourney.pinNumber2.length,
  ]);

  const loadingOrDisabled =
    !tourneyData || isLoading || isLoadingRanking || isLoadingDataTourney;

  // set de estado de modal sin equipo de f5
  useEffect(() => {
    if (tabsTeam === TabType.SecondTab && !userInitialData?.tiene_equipo_f5) {
      setStateTourney((prevState) => ({
        ...prevState,
        showModal: true,
      }));
    }
    return () => {
      setStateTourney((prevState) => ({
        ...prevState,
        showModal: false,
      }));
    };
  }, [tabsTeam, userInitialData]);

  // tabs equipo f11 y f5
  const tabsTeamData: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: GENERICS_WORDS.f11, disabled: loadingOrDisabled },
      { name: GENERICS_WORDS.f5, disabled: loadingOrDisabled },
    ];
  }, [loadingOrDisabled]);

  // manejo de cerrar modal f5
  const handleModalClose = useCallback(() => {
    setStateTourney((prevState) => ({
      ...prevState,
      showModal: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // manejo redireccion a administrar
  const handleEditClick = useCallback(() => {
    navigate(ROUTES.adminTourney);
  }, [navigate]);

  // manejo redireccion a mi equipo f5
  const handleCreateF5Team = useCallback(() => {
    navigate(ROUTES.welcomeF5);
  }, [navigate]);

  // manejo redireccion a estadisticas
  const handleGoStadistics = useCallback(() => {
    navigate(ROUTES.friendsTournamentsStadistics);
  }, [navigate]);

  const iconNameTourney = useMemo(() => {
    return (
      <div className="flex items-center justify-center lg:justify-start">
        <p>{tourneyData?.tourney?.nombre}</p>
        {tourneyData.isVip && (
          <figure className="w-8 ml-3">
            <IconFigure />
          </figure>
        )}
      </div>
    );
  }, [tourneyData.isVip, tourneyData?.tourney?.nombre]);

  const handlePinValidation = async (pin1: string, pin2: string) => {
    const response = await getPinsValidationTDAVIP(pin1, pin2);

    if (!response.estado && 'codError' in response) {
      setStateTourney((prevState) => ({
        ...prevState,
        pinErrorMessage: response.mensaje,
      }));
    }

    return response.estado;
  };

  const handleChangeTDAtoVip = async () => {
    if (!pinsData.isPremium) {
      setStateTourney((prevState) => ({
        ...prevState,
        pinErrorMessage: '',
      }));
      const isValidPins = await handlePinValidation(
        stateTourney.pinNumber1,
        stateTourney.pinNumber2
      );
      if (!isValidPins) {
        return;
      }
    }
    const response = await changeTDAtoVip(
      stateTourney.pinNumber1,
      stateTourney.pinNumber2,
      tourneyData.id
    );

    if (response.estado) {
      toast.show('Convertiste tu torneo actual a un Torneo de Amigos VIP');
      handleSetTourney({
        ...tourneyData,
        isVip: true,
      });
    } else if ('codError' in response) {
      toast.show(response.mensaje);
    }
    onHandleModalChangeTda();
    refetchDataRanking();
  };

  const onHandleModalChangeTda = useCallback(() => {
    setStateTourney((prevState) => ({
      ...prevState,
      showChangeToVipModal: !stateTourney.showChangeToVipModal,
    }));
  }, [stateTourney.showChangeToVipModal]);

  const onHandleVideoModal = useCallback(() => {
    setStateTourney((prevState) => ({
      ...prevState,
      showDesktopVideoModal: !stateTourney.showDesktopVideoModal,
    }));
  }, [stateTourney.showDesktopVideoModal]);

  useUpdateEffect(() => {
    refetchDataTourney();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.selectedCoverDefault, tourneyData.selectedSourceImageCover]);

  const adsConfig = useMemo(() => {
    return tourneyData.isVip
      ? SECTION_NAME_FOR_ADS.torneovip
      : SECTION_NAME_FOR_ADS.torneodeamigos;
  }, [tourneyData.isVip]);

  const isLastMatchDay = useIsLastMatchDay();

  return (
    <>
      <Loader isVisible={loadingOrDisabled} timeout={isLoadingDataTourney} />
      <Layout
        adsSections={adsConfig}
        backButton={isDesktop && true}
        backButtonAction={() => navigate(ROUTES.friendsTournament)}
        className="px-4"
        coverTop={() => tourneyData.isVip && isDesktop && <CoverImage />}
        extraSuperMegaImportantButton={() => {
          return (
            isMobile &&
            tourneyData.isVip && (
              <Button
                className="underline-offset-4 font-bold mb-5"
                type="button"
                variant="text"
                onClick={handleGoStadistics}
              >
                Ver estadísticas del Torneo
              </Button>
            )
          );
        }}
        main={() => (
          <div className="lg:pr-4">
            {isDesktop && (
              <div className="max-h-[214px] h-screen flex flex-col justify-between">
                <h4 className="font-bold text-2xl mt-6">{iconNameTourney}</h4>
                <div className={cn('flex justify-center mb-5', {})}>
                  <Tabs
                    activeTab={tabsTeam}
                    tabs={tabsTeamData}
                    onClick={handleTabsTeam}
                  />
                </div>
              </div>
            )}
            <div className="lg:bg-white lg:rounded-xl">
              <RankingSelector onClickChangeToVip={onHandleModalChangeTda} />
            </div>
            {tourneyData.isVip && isDesktop && (
              <Statistics.Graphics data={tourneyData.statitics} />
            )}
          </div>
        )}
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.friendsTournament);
            },
          },
          centerContent: {
            title: iconNameTourney,
          },
          rightContent: {
            customRightContent: tourneyData.isOwner && !isLastMatchDay && (
              <button
                className="rounded-[.63rem] bg-gdt-gray-9 w-8 h-8 flex justify-center items-center"
                onClick={handleEditClick}
              >
                <PencilIcon />
              </button>
            ),
          },
        }}
        sidebar={() =>
          isDesktop && (
            <SidebarContentView
              tab={tourneyData.subMainTab}
              tourney={tourneyData.tourney}
              onClickChangeToVip={onHandleModalChangeTda}
              onClickOpenVideoModal={onHandleVideoModal}
            />
          )
        }
        tabs={() => {
          return isMobile ? (
            <Tabs
              activeTab={tabsTeam}
              tabs={tabsTeamData}
              onClick={handleTabsTeam}
            />
          ) : null;
        }}
      />
      {/* //MODAL DE PARA ARMADO DE EQUIPO DE F5 */}
      <Modal
        showCloseButton
        controlledOnClose={handleModalClose}
        isControlledOpen={stateTourney.showModal}
        render={() => (
          <>
            <p className="mt-4 tracking-[0.4px]">
              En tu Torneo de Amigos ahora podrás competir con tus conocidos en
              el campeonato de Primera División y en Fútbol 5 Gran DT.
            </p>
            <Button
              className="mt-8 w-full"
              type="button"
              variant="primary"
              onClick={handleCreateF5Team}
            >
              Armá tu equipo de Fútbol 5
            </Button>
          </>
        )}
        size="xs"
        title="Armá un equipo en esta categoría y formá parte de este ranking"
        variant="controlled"
      />
      <Modal
        showCloseButton
        controlledOnClose={onHandleModalChangeTda}
        isControlledOpen={stateTourney.showChangeToVipModal}
        render={() => (
          <div className="flex justify-center flex-row">
            <div className="flex-1 max-w-[370px]">
              {pinsData.isPremium && (
                <p className="text-[14px] p-[15px] text-center text-[#4f4f4f]">
                  Por tener activo tu Pack Premium, podés convertir tu Torneo de
                  Amigos a uno VIP sin cargo.
                </p>
              )}
              {!pinsData.isPremium && (
                <>
                  <Label className="font-normal mt-[20px] mb-[10px] text-[#332F2D]">
                    Ingresá 2 pines
                  </Label>
                  <div className="mb-[10px]">
                    <Form.Field>
                      <InputField
                        behavior="default"
                        maxLength={12}
                        name="pin_1"
                        placeholder="Pin 1"
                        value={stateTourney.pinNumber1}
                        onChange={(event) => {
                          const { value } = event.target;
                          if (
                            String(value).match(
                              ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS
                            ) !== null
                          ) {
                            setStateTourney((prevState) => ({
                              ...prevState,
                              pinNumber1: value.trim(),
                            }));
                          }
                        }}
                      />
                    </Form.Field>
                  </div>
                  <div className="mb-[30px]">
                    <Form.Field>
                      <InputField
                        behavior="default"
                        maxLength={12}
                        name="pin_2"
                        placeholder="Pin 2"
                        value={stateTourney.pinNumber2}
                        onChange={(event) => {
                          const { value } = event.target;
                          if (
                            String(value).match(
                              ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS
                            ) !== null
                          ) {
                            setStateTourney((prevState) => ({
                              ...prevState,
                              pinNumber2: value.trim(),
                            }));
                          }
                        }}
                      />
                    </Form.Field>
                  </div>
                  <div className="m-[5px]">
                    {stateTourney.pinErrorMessage && (
                      <InputError.Label id={'pinErrorID'}>
                        {stateTourney.pinErrorMessage}
                      </InputError.Label>
                    )}
                  </div>
                </>
              )}

              <div className="flex flex-row justify-center space-x-[10px] h-[50px] mt-[10px]">
                <Button
                  className="font-semibold no-underline flex flex-col items-center justify-center text-sm lg:text-base hover:text-gdt-blue w-[50%] px-[2px]"
                  type="button"
                  variant="secondary"
                  onClick={onHandleModalChangeTda}
                >
                  Cancelar
                </Button>
                <Button
                  className="font-semibold no-underline flex flex-col items-center justify-center text-sm lg:text-base w-[50%]"
                  disabled={stateTourney.convertButtonDisabled}
                  type="button"
                  variant="primary"
                  onClick={() => handleChangeTDAtoVip()}
                >
                  Convertir
                </Button>
              </div>

              {isDesktop && (
                <div className="text-center pt-[20px]">
                  <p className="text-[#4F4F4F]">
                    Para saber la diferencia entre un torneo común y un torneo
                    VIP, hacé
                    <span className="inline text-gdt-blue font-bold justify-center pt-[20px] underline hover:cursor-pointer">
                      <span className="pl-[4px]" onClick={onHandleVideoModal}>
                        click acá.
                      </span>
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        size="xs"
        title="Convertir torneo actual a Torneo de Amigos VIP"
        variant="controlled"
      />
      <Modal
        bottomSheetClassName="px-[16px] pt-[20px] pb-[40px]"
        className="px-[1.06rem] lg:px-11 py-10"
        controlledOnClose={onHandleVideoModal}
        isControlledOpen={stateTourney.showDesktopVideoModal}
        render={() => {
          return (
            <div>
              <iframe
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                height="315"
                referrerPolicy="strict-origin-when-cross-origin"
                src="https://www.youtube.com/embed/imDCARfBWak?si=K56cW08Z5N-SYAI4"
                width="100%"
              />
            </div>
          );
        }}
        showCloseButton={true}
        size="xs"
        variant="controlled"
      />
    </>
  );
};
